import React__default, { createContext, useState, useCallback, useMemo, useContext } from 'react';
import { createPortal } from 'react-dom';
import Toast from './toast.js';
import generateId from '../../utils/generate_id.js';
import { toastsOverlay, toastsOuterContainer, toastsContainer } from './style.css.js';

const autoClosePeriod = {
    long: 10000,
    short: 5000,
    off: 0,
};
const kinds = {
    info: {
        kind: 'info',
        icon: 'small-info',
    },
    success: {
        kind: 'success',
        icon: 'small-circular-check',
    },
    warning: {
        kind: 'warning',
        icon: 'small-alert',
    },
    danger: {
        kind: 'danger',
        icon: 'small-circular-cross',
    },
};
const ToastContext = createContext(undefined);
const ToastProvider = (props) => {
    const { children } = props;
    const toastRef = React__default.useRef(null);
    const [toasts, setToasts] = useState([]);
    const [hovered, setHovered] = useState(false);
    const clearToast = useCallback((id) => {
        if (!id)
            return;
        setToasts((prevToasts) => prevToasts.filter((item) => item.id !== id));
    }, []);
    const showToast = useCallback((content, options) => {
        const toast = {
            ...options,
            content,
            id: generateId(),
        };
        return setToasts((prevToasts) => [...prevToasts, toast]);
    }, []);
    const info = useCallback((content, options) => {
        const toast = {
            ...options,
            kind: 'info',
            content,
            id: generateId(),
        };
        return setToasts((prevToasts) => [...prevToasts, toast]);
    }, []);
    const success = useCallback((content, options) => {
        const toast = {
            ...options,
            kind: 'success',
            content,
            id: generateId(),
        };
        return setToasts((prevToasts) => [...prevToasts, toast]);
    }, []);
    const warning = useCallback((content, options) => {
        const toast = {
            ...options,
            kind: 'warning',
            content,
            id: generateId(),
        };
        return setToasts((prevToasts) => [...prevToasts, toast]);
    }, []);
    const danger = useCallback((content, options) => {
        const toast = {
            ...options,
            kind: 'danger',
            content,
            id: generateId(),
        };
        return setToasts((prevToasts) => [...prevToasts, toast]);
    }, []);
    const contextValue = useMemo(() => ({
        showToast,
        info,
        success,
        warning,
        danger,
        toastRef,
    }), [showToast, info, success, warning, danger, toastRef]);
    return (React__default.createElement(ToastContext.Provider, { value: contextValue },
        createPortal(React__default.createElement("div", { className: toastsOverlay },
            React__default.createElement("div", { className: toastsOuterContainer },
                React__default.createElement("div", { ref: toastRef, id: "toaster", onMouseEnter: () => setHovered(true), onMouseLeave: () => setHovered(false), onMouseDownCapture: (event) => event.stopPropagation(), className: toastsContainer }, toasts.map((toast) => (React__default.createElement(Toast, { key: toast.id, ...toast, onClose: () => clearToast(toast.id), hovered: hovered })))))), document.body),
        children));
};
const useToast = () => {
    const context = useContext(ToastContext);
    if (!context) {
        throw new Error('useToast must be used within a ToastProvider');
    }
    return context;
};

export { ToastProvider, autoClosePeriod, kinds, useToast };
